import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import {
    Plus, Trash, ArrowsVertical, FloppyDisk, ArrowLeft, ShareNetwork, Eye, GitBranch,
    CaretUp, CaretDown, CaretRight as CaretRightSmall, PencilSimple, DownloadSimple, Lock,
} from "@phosphor-icons/react";
import { ShareFormDialog } from "@/components/app/ShareFormDialog";
import { AccessConfigDialog } from "@/components/app/AccessConfigDialog";
import { toast } from "sonner";

const FIELD_TYPES = [
    "text","textarea","number","decimal","phone","email",
    "date","datetime","dropdown","multiselect","checkbox",
    "radio","toggle","file","image","lookup","notes","table",
];

const blankField = () => ({
    field_name: "", field_label: "", field_type: "text",
    required: false, unique: false, readonly: false, hidden: false, sensitive: false,
    searchable: false, sortable: false, filterable: false,
    mobile_only: false, desktop_only: false,
    section_id: null, validation_rules: {},
    placeholder: "", help_text: "", order: 0,
    default_value: null, default_group_value_enabled: false,
    visibility_rules: [], options: [],
    lookup_form_id: null, lookup_display_field: null,
    lookup_value_field: null, lookup_filter_field: null, lookup_filter_target_field: null,
    dependent_parent_field: null, dependent_options: {},
    auto_generate: { enabled: false, prefix: "", suffix: "", padding: 3, start: 1 },
    table_columns: [],
});

export default function FormBuilderPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [editing, setEditing] = useState(null);  // field being edited
    const [editorOpen, setEditorOpen] = useState(false);
    const [sectionDlg, setSectionDlg] = useState(false);
    const [secForm, setSecForm] = useState({ title: "", description: "", order: 0 });
    const [editingSection, setEditingSection] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [accessCfgOpen, setAccessCfgOpen] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get(`/forms/${id}`);
            setForm(data.form); setSections(data.sections); setFields(data.fields);
        } catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const startNewField = () => {
        setEditing({ ...blankField(), order: fields.length });
        setEditorOpen(true);
    };
    const startEditField = (f) => {
        setEditing({ ...f, options: f.options || [], visibility_rules: f.visibility_rules || [] });
        setEditorOpen(true);
    };

    const saveField = async () => {
        try {
            const body = { ...editing };
            // sanitize
            if (!body.field_name) return toast.error("Field name is required");
            if (!body.field_label) body.field_label = body.field_name;
            if (editing.id) {
                await api.patch(`/forms/${id}/fields/${editing.id}`, body);
            } else {
                await api.post(`/forms/${id}/fields`, body);
            }
            setEditorOpen(false); setEditing(null);
            await load();
            toast.success("Field saved");
        } catch (e) { toast.error(extractError(e)); }
    };

    const removeField = async (fid) => {
        if (!confirm("Remove this field?")) return;
        try { await api.delete(`/forms/${id}/fields/${fid}`); await load(); toast.success("Field removed"); }
        catch (e) { toast.error(extractError(e)); }
    };

    const moveField = async (fid, dir) => {
        const idx = fields.findIndex((f) => f.id === fid);
        const target = idx + dir;
        if (target < 0 || target >= fields.length) return;
        const reordered = [...fields];
        const [m] = reordered.splice(idx, 1);
        reordered.splice(target, 0, m);
        setFields(reordered);
        await api.post(`/forms/${id}/fields/reorder`, { order: reordered.map((f) => f.id) });
    };

    const saveSection = async () => {
        try {
            if (editingSection) {
                await api.patch(`/forms/${id}/sections/${editingSection.id}`, secForm);
            } else {
                await api.post(`/forms/${id}/sections`, secForm);
            }
            setSectionDlg(false); setEditingSection(null);
            setSecForm({ title: "", description: "", order: 0 });
            await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const delSection = async (sid) => {
        if (!confirm("Delete section? Its fields will be unsectioned.")) return;
        try { await api.delete(`/forms/${id}/sections/${sid}`); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const publish = async () => {
        try {
            const newStatus = form.status === "published" ? "draft" : "published";
            await api.patch(`/forms/${id}`, { status: newStatus });
            await load();
            toast.success(newStatus === "published" ? "Form published" : "Reverted to draft");
        } catch (e) { toast.error(extractError(e)); }
    };

    const togglePublic = async (enabled) => {
        try {
            await api.patch(`/forms/${id}`, { public_enabled: enabled });
            await load();
            toast.success(enabled ? "Form is now public" : "Public access disabled");
        } catch (e) { toast.error(extractError(e)); }
    };

    const updatePin = async (pin) => {
        try {
            await api.patch(`/forms/${id}`, { public_pin: pin });
            await load();
            toast.success(pin ? "PIN set" : "PIN removed");
        } catch (e) { toast.error(extractError(e)); }
    };

    const exportForm = async () => {
        try {
            const { data } = await api.get(`/forms/${id}/export`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${form.form_code || "form"}.formflo.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Form exported");
        } catch (e) { toast.error(extractError(e)); }
    };

    const updateLayoutType = async (layout_type) => {
        try {
            await api.patch(`/forms/${id}`, { layout_type });
            await load();
            toast.success(`Layout set to ${layout_type}`);
        } catch (e) { toast.error(extractError(e)); }
    };

    const openWorkflow = () => navigate(`/forms/${id}/workflow`);

    if (!form) return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;

    return (
        <div>
            <div className="border-b border-border bg-card px-8 py-5 flex items-start justify-between sticky top-0 z-10 gap-4">
                <div className="flex-1 min-w-0">
                    <button onClick={() => navigate("/forms")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="back-to-forms"><ArrowLeft size={12} /> All forms</button>
                    <Input
                        value={form.form_title ?? ""}
                        onChange={(e) => setForm({ ...form, form_title: e.target.value })}
                        onBlur={async (e) => {
                            const v = e.target.value.trim();
                            if (v === (form.form_title || form.form_name)) return;
                            try { await api.patch(`/forms/${id}`, { form_title: v || form.form_name }); await load(); toast.success("Title updated"); }
                            catch (err) { toast.error(extractError(err)); }
                        }}
                        placeholder={form.form_name}
                        className="text-2xl font-bold tracking-tight border-0 px-0 h-auto py-0 focus-visible:ring-0 bg-transparent shadow-none rounded-none -mx-1 w-full max-w-xl"
                        data-testid="builder-form-title-input"
                    />
                    <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">internal name:</span>
                        <code className="text-xs font-mono text-muted-foreground">{form.form_name}</code>
                        <code className="text-xs font-mono text-muted-foreground">·</code>
                        <code className="text-xs font-mono text-muted-foreground">{form.form_code}</code>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{form.status}</Badge>
                        <span className="text-xs font-mono text-muted-foreground">v{form.version}</span>
                        {form.public_enabled && (
                            <Badge className="rounded-sm font-mono text-[10px] bg-success" data-testid="public-badge">PUBLIC</Badge>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={form.layout_type || "single"} onValueChange={updateLayoutType}>
                        <SelectTrigger className="w-40 rounded-sm" data-testid="layout-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="single">Single form</SelectItem>
                            <SelectItem value="stepper">Multi-step</SelectItem>
                            <SelectItem value="tabs">Tabbed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={openWorkflow} className="rounded-sm" data-testid="builder-workflow-btn">
                        <GitBranch size={14} className="mr-1" /> Workflow{form.workflow_enabled ? ` (${(form.workflow_steps || []).length})` : ""}
                    </Button>
                    <Button variant="outline" onClick={() => setAccessCfgOpen(true)} className="rounded-sm" data-testid="builder-access-cfg-btn">
                        Defaults
                    </Button>
                    <Button variant="outline" onClick={exportForm} className="rounded-sm" data-testid="builder-export-btn">
                        <DownloadSimple size={14} className="mr-1" /> Export
                    </Button>
                    <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-1.5" data-testid="public-toggle-wrapper">
                        <span className="text-xs font-medium">Public</span>
                        <Switch
                            checked={!!form.public_enabled}
                            onCheckedChange={togglePublic}
                            disabled={form.status !== "published"}
                            data-testid="public-toggle"
                        />
                        {form.public_enabled && (
                            <Input
                                value={form.public_pin || ""}
                                onChange={(e) => setForm({ ...form, public_pin: e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12) })}
                                onBlur={(e) => updatePin(e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12))}
                                placeholder="PIN (optional)"
                                className="rounded-sm h-7 w-32 text-xs font-mono"
                                data-testid="public-pin-input"
                            />
                        )}
                    </div>
                    <Link to={`/forms/${id}/fill`}><Button variant="outline" className="rounded-sm" data-testid="builder-fill-btn"><Eye size={14} className="mr-1" /> Fill</Button></Link>
                    <Link to={`/forms/${id}/records`}><Button variant="outline" className="rounded-sm" data-testid="builder-records-btn">View records</Button></Link>
                    <Button variant="outline" onClick={() => setShareOpen(true)} className="rounded-sm" data-testid="builder-share-btn"><ShareNetwork size={14} className="mr-1" /> Share</Button>
                    <Button onClick={publish} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="publish-btn">
                        {form.status === "published" ? "Unpublish" : "Publish"}
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[260px_1fr_260px] min-h-[calc(100vh-90px)]">
                {/* LEFT: Field types + sections */}
                <aside className="border-r border-border bg-card p-6" data-testid="builder-blocks-panel">
                    <div className="label-overline mb-3">Add field</div>
                    <Button onClick={startNewField} className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90 mb-6" data-testid="add-field-btn"><Plus size={14} className="mr-1" /> New field</Button>

                    <div className="border-t border-border pt-6">
                        <div className="flex items-center justify-between mb-3">
                            <div className="label-overline">Sections</div>
                            <Button size="icon" variant="ghost" className="rounded-sm h-6 w-6" onClick={() => { setEditingSection(null); setSecForm({ title: "", description: "", order: sections.length }); setSectionDlg(true); }} data-testid="add-section-btn"><Plus size={12} /></Button>
                        </div>
                        <div className="space-y-1">
                            {sections.map((s) => (
                                <div key={s.id} className="flex items-center justify-between text-sm group">
                                    <button onClick={() => { setEditingSection(s); setSecForm({ title: s.title, description: s.description || "", order: s.order }); setSectionDlg(true); }} className="text-left flex-1 hover:underline" data-testid={`section-${s.id}`}>{s.title}</button>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => delSection(s.id)}><Trash size={12} /></Button>
                                </div>
                            ))}
                            {sections.length === 0 && <div className="text-xs text-muted-foreground">No sections</div>}
                        </div>
                    </div>
                </aside>

                {/* CENTER: Live preview */}
                <div className="p-8 bg-secondary/30 overflow-auto">
                    <div className="max-w-2xl mx-auto">
                        <div className="label-overline mb-2">Preview</div>
                        <h2 className="text-2xl font-bold tracking-tight mb-6">{form.form_title || form.form_name}</h2>
                        {fields.length === 0 ? (
                            <div className="border border-dashed border-border p-12 text-center rounded-sm">
                                <div className="text-sm text-muted-foreground">No fields yet. Add your first field →</div>
                            </div>
                        ) : (
                            <div className="brut-section p-6 bg-card" data-testid="preview-area">
                                <DynamicFormRenderer sections={sections} fields={fields} data={{}} onChange={() => {}} readOnly layoutMode={form.layout_type || "single"} />
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT: Fields list / properties — grouped by section */}
                <aside className="border-l border-border bg-card overflow-auto" data-testid="builder-fields-panel">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fields · {fields.length}</div>
                        <Button size="icon" variant="ghost" className="h-6 w-6 rounded-sm" onClick={startNewField} data-testid="add-field-btn-right" title="Add field"><Plus size={12} /></Button>
                    </div>
                    {fields.length === 0 && <div className="text-xs text-muted-foreground p-4">Click + to add your first field.</div>}
                    {fields.length > 0 && (
                        <FieldTree
                            sections={sections}
                            fields={fields}
                            collapsedSections={collapsedSections}
                            toggleSection={(sid) => setCollapsedSections((prev) => ({ ...prev, [sid]: !prev[sid] }))}
                            onEdit={startEditField}
                            onDelete={removeField}
                            onMove={moveField}
                        />
                    )}
                </aside>
            </div>

            {/* Field editor */}
            <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="field-editor-dialog">
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit field" : "New field"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label className="label-overline">Field name</Label><Input value={editing.field_name} onChange={(e) => setEditing({ ...editing, field_name: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })} className="mt-2 rounded-sm font-mono" data-testid="field-name-input" /></div>
                                <div><Label className="label-overline">Label</Label><Input value={editing.field_label} onChange={(e) => setEditing({ ...editing, field_label: e.target.value })} className="mt-2 rounded-sm" data-testid="field-label-input" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-overline">Type</Label>
                                    <Select value={editing.field_type} onValueChange={(v) => setEditing({ ...editing, field_type: v })}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="field-type-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="label-overline">Section</Label>
                                    <Select value={editing.section_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, section_id: v === "__none__" ? null : v })}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="field-section-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">No section</SelectItem>
                                            {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div><Label className="label-overline">Placeholder</Label><Input value={editing.placeholder || ""} onChange={(e) => setEditing({ ...editing, placeholder: e.target.value })} className="mt-2 rounded-sm" /></div>
                            <div><Label className="label-overline">Help text</Label><Textarea value={editing.help_text || ""} onChange={(e) => setEditing({ ...editing, help_text: e.target.value })} className="mt-2 rounded-sm" /></div>

                            {(editing.field_type === "dropdown" || editing.field_type === "multiselect" || editing.field_type === "radio") && (
                                <div>
                                    <Label className="label-overline">Options</Label>
                                    <div className="space-y-2 mt-2">
                                        {(editing.options || []).map((o, i) => (
                                            <div key={o._uid || `opt-${i}-${o.value || o.label || ""}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                                <Input value={o.label} onChange={(e) => { const next = [...editing.options]; next[i] = { ...o, label: e.target.value }; setEditing({ ...editing, options: next }); }} placeholder="Label" className="rounded-sm" />
                                                <Input value={o.value} onChange={(e) => { const next = [...editing.options]; next[i] = { ...o, value: e.target.value }; setEditing({ ...editing, options: next }); }} placeholder="value" className="rounded-sm font-mono text-xs" />
                                                <Button variant="ghost" size="icon" onClick={() => setEditing({ ...editing, options: editing.options.filter((_, j) => j !== i) })}><Trash size={14} /></Button>
                                            </div>
                                        ))}
                                        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setEditing({ ...editing, options: [...(editing.options || []), { _uid: `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label: "", value: "" }] })} data-testid="add-option-btn">Add option</Button>
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-border pt-4">
                                <Label className="label-overline mb-3 block">Flags</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        ["required", "Required"],
                                        ["unique", "Unique"],
                                        ["readonly", "Read-only"],
                                        ["hidden", "Hidden"],
                                        ["searchable", "Searchable"],
                                        ["sortable", "Sortable"],
                                        ["filterable", "Filterable"],
                                        ["mobile_only", "Mobile only"],
                                        ["desktop_only", "Desktop only"],
                                        ["default_group_value_enabled", "Group default"],
                                    ].map(([k, l]) => (
                                        <label key={k} className="flex items-center justify-between gap-2 text-sm">
                                            <span>{l}</span>
                                            <Switch checked={!!editing[k]} onCheckedChange={(c) => setEditing({ ...editing, [k]: c })} data-testid={`flag-${k}`} />
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {["text", "textarea", "number", "phone", "email"].includes(editing.field_type) && (
                                <div className="border-t border-border pt-4" data-testid="sensitive-block">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <Label className="label-overline">Sensitive · encrypt at rest</Label>
                                            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                                                Stores the value encrypted (Fernet/AES) in the database. Users with read access still see the plaintext. Free-text search and sorting are disabled for this field; equality (=) filters keep working via a blind index.
                                            </p>
                                        </div>
                                        <Switch
                                            checked={!!editing.sensitive}
                                            onCheckedChange={(c) => setEditing({
                                                ...editing,
                                                sensitive: c,
                                                // A sensitive field can never be free-text searched or sorted.
                                                searchable: c ? false : editing.searchable,
                                                sortable: c ? false : editing.sortable,
                                            })}
                                            data-testid="flag-sensitive"
                                        />
                                    </div>
                                </div>
                            )}

                            {(editing.field_type === "text" || editing.field_type === "number") && (
                                <div className="border-t border-border pt-4" data-testid="auto-generate-block">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <Label className="label-overline">Auto-generate value</Label>
                                            <p className="text-[11px] text-muted-foreground mt-1">Each new record gets a unique sequence like <code className="font-mono bg-secondary px-1 rounded-sm">PO-001</code>, <code className="font-mono bg-secondary px-1 rounded-sm">PO-002</code>…</p>
                                        </div>
                                        <Switch
                                            checked={!!editing.auto_generate?.enabled}
                                            onCheckedChange={(c) => setEditing({ ...editing, auto_generate: { ...(editing.auto_generate || { prefix: "", suffix: "", padding: 3, start: 1 }), enabled: c } })}
                                            data-testid="auto-generate-toggle"
                                        />
                                    </div>
                                    {editing.auto_generate?.enabled && (
                                        <div className="grid grid-cols-4 gap-3 mt-2" data-testid="auto-generate-config">
                                            <div>
                                                <Label className="label-overline">Prefix</Label>
                                                <Input value={editing.auto_generate.prefix || ""} onChange={(e) => setEditing({ ...editing, auto_generate: { ...editing.auto_generate, prefix: e.target.value } })} placeholder="PO-" className="mt-2 rounded-sm font-mono" data-testid="auto-generate-prefix" />
                                            </div>
                                            <div>
                                                <Label className="label-overline">Suffix</Label>
                                                <Input value={editing.auto_generate.suffix || ""} onChange={(e) => setEditing({ ...editing, auto_generate: { ...editing.auto_generate, suffix: e.target.value } })} placeholder="" className="mt-2 rounded-sm font-mono" data-testid="auto-generate-suffix" />
                                            </div>
                                            <div>
                                                <Label className="label-overline">Padding</Label>
                                                <Input type="number" min={0} max={10} value={editing.auto_generate.padding ?? 3} onChange={(e) => setEditing({ ...editing, auto_generate: { ...editing.auto_generate, padding: Math.max(0, Math.min(10, Number(e.target.value) || 0)) } })} className="mt-2 rounded-sm font-mono" data-testid="auto-generate-padding" />
                                            </div>
                                            <div>
                                                <Label className="label-overline">Start at</Label>
                                                <Input type="number" min={1} value={editing.auto_generate.start ?? 1} onChange={(e) => setEditing({ ...editing, auto_generate: { ...editing.auto_generate, start: Math.max(1, Number(e.target.value) || 1) } })} className="mt-2 rounded-sm font-mono" data-testid="auto-generate-start" />
                                            </div>
                                            <div className="col-span-4 text-xs text-muted-foreground border border-dashed border-border rounded-sm p-2 bg-secondary/30" data-testid="auto-generate-preview">
                                                Preview: <span className="font-mono font-semibold text-foreground">
                                                    {(editing.auto_generate.prefix || "")}
                                                    {String(editing.auto_generate.start ?? 1).padStart(editing.auto_generate.padding ?? 0, "0")}
                                                    {(editing.auto_generate.suffix || "")}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {editing.field_type === "table" && (
                                <div className="border-t border-border pt-4" data-testid="table-columns-block">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <Label className="label-overline">Table columns</Label>
                                            <p className="text-[11px] text-muted-foreground mt-1">Define the columns for each row. Text and number types are supported; toggle Σ on a number column to auto-total it.</p>
                                        </div>
                                        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setEditing({ ...editing, table_columns: [...(editing.table_columns || []), { _uid: `tc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: `col_${(editing.table_columns || []).length + 1}`, label: `Column ${(editing.table_columns || []).length + 1}`, type: "text", sum: false, required: false }] })} data-testid="table-add-column-btn">
                                            <Plus size={12} className="mr-1" /> Add column
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {(editing.table_columns || []).length === 0 && (
                                            <div className="border border-dashed border-border rounded-sm p-3 text-xs text-muted-foreground text-center">
                                                No columns yet. Click "Add column" to start.
                                            </div>
                                        )}
                                        {(editing.table_columns || []).map((c, i) => (
                                            <div key={c._uid || `${c.name}-${i}`} className="space-y-2 border border-border rounded-sm p-2" data-testid={`table-col-row-${i}`}>
                                                <div className="grid grid-cols-[1fr_1fr_120px_60px_60px_36px] gap-2 items-center">
                                                    <Input value={c.label} onChange={(e) => { const next = [...editing.table_columns]; next[i] = { ...c, label: e.target.value }; setEditing({ ...editing, table_columns: next }); }} placeholder="Label (e.g. Item)" className="rounded-sm" />
                                                    <Input value={c.name} onChange={(e) => { const next = [...editing.table_columns]; next[i] = { ...c, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") }; setEditing({ ...editing, table_columns: next }); }} placeholder="key" className="rounded-sm font-mono text-xs" />
                                                    <Select value={c.type} onValueChange={(v) => { const next = [...editing.table_columns]; next[i] = { ...c, type: v, sum: v === "number" ? c.sum : false, options: v === "dropdown" ? (c.options || []) : [] }; setEditing({ ...editing, table_columns: next }); }}>
                                                        <SelectTrigger className="rounded-sm h-9" data-testid={`table-col-type-${i}`}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="text">text</SelectItem>
                                                            <SelectItem value="textarea">textarea</SelectItem>
                                                            <SelectItem value="number">number</SelectItem>
                                                            <SelectItem value="date">date</SelectItem>
                                                            <SelectItem value="dropdown">dropdown</SelectItem>
                                                            <SelectItem value="lookup">lookup</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <label className="flex items-center justify-center gap-1 text-xs text-muted-foreground" title="Auto-sum">
                                                        Σ <Switch checked={!!c.sum} disabled={c.type !== "number"} onCheckedChange={(v) => { const next = [...editing.table_columns]; next[i] = { ...c, sum: v }; setEditing({ ...editing, table_columns: next }); }} data-testid={`table-col-sum-${i}`} />
                                                    </label>
                                                    <label className="flex items-center justify-center gap-1 text-xs text-muted-foreground" title="Required">
                                                        * <Switch checked={!!c.required} onCheckedChange={(v) => { const next = [...editing.table_columns]; next[i] = { ...c, required: v }; setEditing({ ...editing, table_columns: next }); }} data-testid={`table-col-req-${i}`} />
                                                    </label>
                                                    <Button type="button" variant="ghost" size="icon" onClick={() => setEditing({ ...editing, table_columns: editing.table_columns.filter((_, j) => j !== i) })} data-testid={`table-col-del-${i}`}>
                                                        <Trash size={14} />
                                                    </Button>
                                                </div>
                                                {c.type === "dropdown" && (
                                                    <div className="bg-secondary/30 rounded-sm p-2 border border-dashed border-border" data-testid={`table-col-options-${i}`}>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">Dropdown options</div>
                                                            <Button type="button" variant="ghost" size="sm" className="rounded-sm h-6 text-xs" onClick={() => { const next = [...editing.table_columns]; next[i] = { ...c, options: [...(c.options || []), { value: `opt_${(c.options || []).length + 1}`, label: `Option ${(c.options || []).length + 1}` }] }; setEditing({ ...editing, table_columns: next }); }} data-testid={`table-col-${i}-add-option`}>
                                                                <Plus size={10} className="mr-1" /> Add
                                                            </Button>
                                                        </div>
                                                        {(c.options || []).length === 0 ? (
                                                            <div className="text-[11px] text-muted-foreground italic px-1">No options yet.</div>
                                                        ) : (
                                                            <div className="space-y-1">
                                                                {(c.options || []).map((opt, oi) => (
                                                                    <div key={`${oi}-${opt.value}`} className="grid grid-cols-[1fr_1fr_36px] gap-1 items-center">
                                                                        <Input value={opt.label} onChange={(e) => { const next = [...editing.table_columns]; const opts = [...(c.options || [])]; opts[oi] = { ...opt, label: e.target.value }; next[i] = { ...c, options: opts }; setEditing({ ...editing, table_columns: next }); }} placeholder="Label" className="rounded-sm h-7 text-xs" data-testid={`table-col-${i}-opt-${oi}-label`} />
                                                                        <Input value={opt.value} onChange={(e) => { const next = [...editing.table_columns]; const opts = [...(c.options || [])]; opts[oi] = { ...opt, value: e.target.value }; next[i] = { ...c, options: opts }; setEditing({ ...editing, table_columns: next }); }} placeholder="value" className="rounded-sm h-7 text-xs font-mono" data-testid={`table-col-${i}-opt-${oi}-value`} />
                                                                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const next = [...editing.table_columns]; next[i] = { ...c, options: (c.options || []).filter((_, j) => j !== oi) }; setEditing({ ...editing, table_columns: next }); }} data-testid={`table-col-${i}-opt-${oi}-del`}>
                                                                            <Trash size={11} />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {c.type === "lookup" && (
                                                    <TableLookupConfig
                                                        column={c}
                                                        onChange={(patch) => {
                                                            const next = [...editing.table_columns];
                                                            next[i] = { ...c, ...patch };
                                                            setEditing({ ...editing, table_columns: next });
                                                        }}
                                                        currentFormId={id}
                                                        testid={`table-col-${i}-lookup`}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {editing.field_type === "lookup" && (
                                <LookupFieldConfig editing={editing} setEditing={setEditing} currentFormId={id} fields={fields} />
                            )}

                            {editing.field_type === "dropdown" && (
                                <DependentDropdownConfig editing={editing} setEditing={setEditing} fields={fields} />
                            )}

                            <DialogFooter><Button onClick={saveField} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-field-btn"><FloppyDisk size={14} className="mr-1" /> Save field</Button></DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Section editor */}
            <Dialog open={sectionDlg} onOpenChange={setSectionDlg}>
                <DialogContent className="rounded-sm" data-testid="section-dialog">
                    <DialogHeader><DialogTitle>{editingSection ? "Edit section" : "New section"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label className="label-overline">Title</Label><Input value={secForm.title} onChange={(e) => setSecForm({ ...secForm, title: e.target.value })} className="mt-2 rounded-sm" data-testid="section-title-input" /></div>
                        <div><Label className="label-overline">Description</Label><Textarea value={secForm.description} onChange={(e) => setSecForm({ ...secForm, description: e.target.value })} className="mt-2 rounded-sm" /></div>
                        <div><Label className="label-overline">Order</Label><Input type="number" value={secForm.order} onChange={(e) => setSecForm({ ...secForm, order: Number(e.target.value) })} className="mt-2 rounded-sm w-32" /></div>
                        <DialogFooter><Button onClick={saveSection} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-section-btn">Save</Button></DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            <ShareFormDialog form={form} open={shareOpen} onOpenChange={setShareOpen} onSaved={load} />

            <AccessConfigDialog
                formId={id}
                open={accessCfgOpen}
                onOpenChange={setAccessCfgOpen}
                fields={fields}
                accessGroupIds={form.access_group_ids || []}
                accessRoleIds={form.access_role_ids || []}
            />
        </div>
    );
}



/* ------------------------------------------------------------------
 * FieldTree — collapsible section → field tree for the right sidebar
 * ------------------------------------------------------------------ */
function FieldTree({ sections, fields, collapsedSections, toggleSection, onEdit, onDelete, onMove }) {
    // Map: section_id -> ordered list of fields. Sectionless fields go under
    // a synthetic "__all__" bucket displayed as "All Fields".
    const grouped = {};
    fields.forEach((f) => {
        const sid = f.section_id || "__all__";
        if (!grouped[sid]) grouped[sid] = [];
        grouped[sid].push(f);
    });

    // Build the render order: every real section in `sections` order, then
    // the synthetic "All Fields" bucket if it has anything.
    const sortedSections = [...(sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const groups = [];
    sortedSections.forEach((s) => {
        if (grouped[s.id] && grouped[s.id].length) {
            groups.push({ id: s.id, title: s.title, fields: grouped[s.id], synthetic: false });
        }
    });
    if (grouped["__all__"] && grouped["__all__"].length) {
        groups.push({ id: "__all__", title: sections?.length ? "Unsectioned" : "All Fields", fields: grouped["__all__"], synthetic: true });
    }

    return (
        <div className="divide-y divide-border" data-testid="field-tree">
            {groups.map((g) => {
                const collapsed = !!collapsedSections[g.id];
                return (
                    <div key={g.id} data-testid={`field-tree-group-${g.id}`}>
                        <button
                            type="button"
                            onClick={() => toggleSection(g.id)}
                            className="w-full flex items-center justify-between px-3 py-2 bg-secondary/30 hover:bg-secondary/60 text-left"
                            data-testid={`field-tree-toggle-${g.id}`}
                        >
                            <div className="flex items-center gap-1.5 min-w-0">
                                {collapsed ? <CaretRightSmall size={12} /> : <CaretDown size={12} />}
                                <span className="text-xs font-semibold uppercase tracking-wider truncate">{g.title}</span>
                                {g.synthetic && <span className="text-[10px] text-muted-foreground">(no section)</span>}
                            </div>
                            <span className="text-[10px] font-mono text-muted-foreground">{g.fields.length}</span>
                        </button>
                        {!collapsed && (
                            <ul className="divide-y divide-border">
                                {g.fields.map((f) => {
                                    const i = fields.findIndex((x) => x.id === f.id);
                                    return (
                                        <li key={f.id} className="px-3 py-2 hover:bg-secondary/40 flex items-center gap-2 group" data-testid={`field-row-${f.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 text-sm font-medium leading-tight truncate">
                                                    <span className="truncate">{f.field_label}</span>
                                                    {f.required && <span className="text-destructive text-xs" title="required">*</span>}
                                                    {f.sensitive && <Lock size={11} className="text-emerald-600 shrink-0" title="Encrypted at rest" />}
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground mt-0.5">
                                                    <span className="px-1 py-px bg-secondary rounded">{f.field_type}</span>
                                                    <span className="truncate">{f.field_name}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" disabled={i === 0} onClick={() => onMove(f.id, -1)} data-testid={`up-${f.id}`} title="Move up"><CaretUp size={12} /></Button>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" disabled={i === fields.length - 1} onClick={() => onMove(f.id, 1)} data-testid={`down-${f.id}`} title="Move down"><CaretDown size={12} /></Button>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={() => onEdit(f)} data-testid={`edit-${f.id}`} title="Edit field"><PencilSimple size={12} /></Button>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm text-destructive hover:text-destructive" onClick={() => onDelete(f.id)} data-testid={`del-${f.id}`} title="Delete field"><Trash size={12} /></Button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    );
}



/**
 * LookupFieldConfig — admin-only field editor sub-block. Lets the builder
 * configure WHICH form this lookup pulls from, which field is the display
 * label, optional value field, and an optional parent-field filter to make
 * the lookup dependent on another field's value.
 */
function LookupFieldConfig({ editing, setEditing, currentFormId, fields }) {
    const [targetForms, setTargetForms] = useState([]);
    const [targetFields, setTargetFields] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/lookup/forms`);
                if (active) setTargetForms((data || []).filter((f) => f.id !== currentFormId));
            } catch { /* swallow */ }
        })();
        return () => { active = false; };
    }, [currentFormId]);

    useEffect(() => {
        let active = true;
        if (!editing.lookup_form_id) { setTargetFields([]); return; }
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/lookup/forms/${editing.lookup_form_id}/fields`);
                if (active) setTargetFields(data || []);
            } catch {
                if (active) setTargetFields([]);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [editing.lookup_form_id]);

    return (
        <div className="border-t border-border pt-4" data-testid="lookup-config-block">
            <div className="mb-3">
                <Label className="label-overline">Lookup configuration</Label>
                <p className="text-[11px] text-muted-foreground mt-1">Pulls live records from another form. The picker shows the configured display column; the stored value defaults to that record&apos;s id.</p>
            </div>
            <div className="space-y-3">
                <div>
                    <Label className="text-xs">Target form</Label>
                    <Select value={editing.lookup_form_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, lookup_form_id: v === "__none__" ? null : v, lookup_display_field: null, lookup_value_field: null, lookup_filter_target_field: null })}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="lookup-target-form-select"><SelectValue placeholder="Pick a form…" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {targetForms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_title || f.form_name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                {editing.lookup_form_id && (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Display field <span className="text-destructive">*</span></Label>
                                <Select value={editing.lookup_display_field || "__none__"} onValueChange={(v) => setEditing({ ...editing, lookup_display_field: v === "__none__" ? null : v })}>
                                    <SelectTrigger className="mt-1 rounded-sm" data-testid="lookup-display-field-select"><SelectValue placeholder={loading ? "Loading…" : "Pick a column"} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— None —</SelectItem>
                                        {targetFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label} <span className="text-[10px] opacity-60 font-mono ml-1">{f.field_type}</span></SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-muted-foreground mt-1">What the user sees in the dropdown.</p>
                            </div>
                            <div>
                                <Label className="text-xs">Stored value field <span className="text-muted-foreground text-[10px]">(optional)</span></Label>
                                <Select value={editing.lookup_value_field || "__id__"} onValueChange={(v) => setEditing({ ...editing, lookup_value_field: v === "__id__" ? null : v })}>
                                    <SelectTrigger className="mt-1 rounded-sm" data-testid="lookup-value-field-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__id__">Record id (recommended)</SelectItem>
                                        {targetFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-muted-foreground mt-1">What gets stored on save. Stick with record id unless you have a reason.</p>
                            </div>
                        </div>
                        <div className="border border-dashed border-border rounded-sm p-3 bg-secondary/30">
                            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Optional: make this a dependent lookup</div>
                            <p className="text-[10px] text-muted-foreground mb-2">Only show records where a column on the target form equals the value of a field on THIS form.</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Parent field (this form)</Label>
                                    <Select value={editing.lookup_filter_field || "__none__"} onValueChange={(v) => setEditing({ ...editing, lookup_filter_field: v === "__none__" ? null : v })}>
                                        <SelectTrigger className="mt-1 rounded-sm" data-testid="lookup-parent-field-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— None —</SelectItem>
                                            {fields.filter((f) => f.field_name !== editing.field_name).map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs">Compared against (target form)</Label>
                                    <Select value={editing.lookup_filter_target_field || "__none__"} onValueChange={(v) => setEditing({ ...editing, lookup_filter_target_field: v === "__none__" ? null : v })}>
                                        <SelectTrigger className="mt-1 rounded-sm" data-testid="lookup-target-filter-field-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— None —</SelectItem>
                                            {targetFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * DependentDropdownConfig — admin-only sub-block on dropdown fields.
 * Lets the builder pick a parent dropdown and configure a per-parent-value
 * options map. Child is locked until parent is filled.
 */
function DependentDropdownConfig({ editing, setEditing, fields }) {
    const parents = fields.filter((f) => f.field_type === "dropdown" && f.field_name !== editing.field_name);
    const map = editing.dependent_options || {};
    const setMap = (k, opts) => setEditing({ ...editing, dependent_options: { ...map, [k]: opts } });
    const parent = parents.find((p) => p.field_name === editing.dependent_parent_field);
    const parentValues = parent ? (parent.options || []) : [];

    return (
        <div className="border-t border-border pt-4" data-testid="dependent-config-block">
            <div className="mb-3">
                <Label className="label-overline">Dependent dropdown <span className="text-muted-foreground text-[10px]">(optional)</span></Label>
                <p className="text-[11px] text-muted-foreground mt-1">Show different options based on the value picked in another dropdown field. The child stays disabled until the parent is filled.</p>
            </div>
            <div className="space-y-3">
                <div>
                    <Label className="text-xs">Parent dropdown</Label>
                    <Select value={editing.dependent_parent_field || "__none__"} onValueChange={(v) => setEditing({ ...editing, dependent_parent_field: v === "__none__" ? null : v })}>
                        <SelectTrigger className="mt-1 rounded-sm" data-testid="dep-parent-select"><SelectValue placeholder="No parent (regular dropdown)" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">— None (regular dropdown) —</SelectItem>
                            {parents.length === 0 && <SelectItem value="__empty__" disabled>No other dropdown fields exist</SelectItem>}
                            {parents.map((p) => <SelectItem key={p.id} value={p.field_name}>{p.field_label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                {editing.dependent_parent_field && parentValues.length > 0 && (
                    <div className="border border-dashed border-border rounded-sm p-3 bg-secondary/30 space-y-3">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Options per parent value</div>
                        {parentValues.map((pv) => {
                            const opts = map[pv.value] || [];
                            return (
                                <div key={pv.value} className="border border-border rounded-sm p-2 bg-card" data-testid={`dep-block-${pv.value}`}>
                                    <div className="text-xs font-medium mb-2">When parent = <code className="font-mono text-[11px] bg-secondary px-1 rounded-sm">{pv.label}</code></div>
                                    <div className="space-y-1">
                                        {opts.map((o, i) => (
                                            <div key={`${i}-${o.value}`} className="grid grid-cols-[1fr_1fr_36px] gap-1">
                                                <Input value={o.label} onChange={(e) => { const next = [...opts]; next[i] = { ...o, label: e.target.value }; setMap(pv.value, next); }} placeholder="Label" className="rounded-sm h-7 text-xs" data-testid={`dep-${pv.value}-${i}-label`} />
                                                <Input value={o.value} onChange={(e) => { const next = [...opts]; next[i] = { ...o, value: e.target.value }; setMap(pv.value, next); }} placeholder="value" className="rounded-sm h-7 text-xs font-mono" data-testid={`dep-${pv.value}-${i}-value`} />
                                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setMap(pv.value, opts.filter((_, j) => j !== i))} data-testid={`dep-${pv.value}-${i}-del`}><Trash size={11} /></Button>
                                            </div>
                                        ))}
                                        <Button type="button" variant="ghost" size="sm" className="rounded-sm h-7 text-xs" onClick={() => setMap(pv.value, [...opts, { value: `opt_${opts.length + 1}`, label: `Option ${opts.length + 1}` }])} data-testid={`dep-${pv.value}-add`}><Plus size={11} className="mr-1" /> Add option</Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {editing.dependent_parent_field && parentValues.length === 0 && (
                    <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3">The selected parent dropdown has no options yet. Add options to it first, then map them here.</div>
                )}
            </div>
        </div>
    );
}


/**
 * TableLookupConfig — admin-only sub-block when a table column type=lookup.
 * Lets the builder pick the target form + display field + (optional) stored value field.
 */
function TableLookupConfig({ column, onChange, currentFormId, testid }) {
    const [forms, setForms] = useState([]);
    const [targetFields, setTargetFields] = useState([]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/lookup/forms`);
                if (active) setForms((data || []).filter((f) => f.id !== currentFormId));
            } catch { /* swallow */ }
        })();
        return () => { active = false; };
    }, [currentFormId]);

    useEffect(() => {
        let active = true;
        if (!column.lookup_form_id) { setTargetFields([]); return; }
        (async () => {
            try {
                const { data } = await api.get(`/lookup/forms/${column.lookup_form_id}/fields`);
                if (active) setTargetFields(data || []);
            } catch { if (active) setTargetFields([]); }
        })();
        return () => { active = false; };
    }, [column.lookup_form_id]);

    return (
        <div className="bg-secondary/30 rounded-sm p-2 border border-dashed border-border space-y-2" data-testid={testid}>
            <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">Lookup column configuration</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Select value={column.lookup_form_id || "__none__"} onValueChange={(v) => onChange({ lookup_form_id: v === "__none__" ? null : v, lookup_display_field: null, lookup_value_field: null })}>
                    <SelectTrigger className="rounded-sm h-8" data-testid={`${testid}-target-form`}><SelectValue placeholder="Target form" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_title || f.form_name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={column.lookup_display_field || "__none__"} onValueChange={(v) => onChange({ lookup_display_field: v === "__none__" ? null : v })} disabled={!column.lookup_form_id}>
                    <SelectTrigger className="rounded-sm h-8" data-testid={`${testid}-display`}><SelectValue placeholder="Display field" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {targetFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={column.lookup_value_field || "__id__"} onValueChange={(v) => onChange({ lookup_value_field: v === "__id__" ? null : v })} disabled={!column.lookup_form_id}>
                    <SelectTrigger className="rounded-sm h-8" data-testid={`${testid}-value`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__id__">Record id (recommended)</SelectItem>
                        {targetFields.map((f) => <SelectItem key={f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}